import { useState, useMemo } from 'react';
import { Alert, Box, Card, CardContent, CardHeader, Chip, Collapse, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import EventIcon from '@mui/icons-material/Event';

// ── Relative time helper ──
const relativeTime = (date) => {
  if (!date) return null;
  const now = new Date();
  const d = new Date(date);
  const diffMs = d - now;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return { text: 'Today', color: 'info' };
  if (diffDays === 1) return { text: 'Tomorrow', color: 'info' };
  if (diffDays === -1) return { text: 'Yesterday', color: 'default' };
  if (diffDays > 1 && diffDays <= 5) return { text: `in ${diffDays} days`, color: 'warning' };
  if (diffDays > 5 && diffDays <= 14) return { text: `in ${diffDays} days`, color: 'info' };
  if (diffDays > 14) return { text: `in ${diffDays} days`, color: 'success' };
  if (diffDays < -1 && diffDays >= -7) return { text: `${Math.abs(diffDays)} days ago`, color: 'default' };
  if (diffDays < -7) return { text: `${Math.abs(diffDays)} days ago`, color: 'default' };
  return { text: `${Math.abs(diffDays)} days ago`, color: 'default' };
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ── Urgency scoring: higher = more urgent ──
const getUrgency = (key, date, loan) => {
  if (!date) return null;
  const now = new Date();
  const d = new Date(date);
  const daysUntil = Math.round((d - now) / (1000 * 60 * 60 * 24));

  // Lock expiring soon
  if (key === 'rateLockExpires' && daysUntil >= 0 && daysUntil <= 5)
    return { level: 'danger', score: 100 - daysUntil, message: `Lock expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}!` };
  if (key === 'rateLockExpires' && daysUntil >= 6 && daysUntil <= 10)
    return { level: 'warning', score: 50 - daysUntil, message: `Lock expires in ${daysUntil} days` };

  // LE deadline
  if (key === 'loanEstimateNeedsToBeSentBy' && daysUntil >= 0 && daysUntil <= 3)
    return { level: 'danger', score: 95 - daysUntil, message: `LE must be sent within ${daysUntil} day${daysUntil !== 1 ? 's' : ''}!` };

  // Closing approaching
  if (key === 'closingDate' && daysUntil >= 0 && daysUntil <= 7)
    return { level: 'warning', score: 80 - daysUntil, message: `Closing in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}` };

  // NOI expiration
  if (key === 'noticeOfIncompletenessExpirationDate' && daysUntil >= 0 && daysUntil <= 10)
    return { level: 'danger', score: 90 - daysUntil, message: `NOI expires in ${daysUntil} days — loan may be closed incomplete` };

  // Overdue items (date in the past where we expect completion)
  if (key === 'appraisalOrdered' && !loan?.keyDatesObj?.appraisalCompletedDate) {
    const daysSinceOrder = Math.abs(daysUntil);
    if (daysSinceOrder > 14)
      return { level: 'warning', score: 60, message: `Appraisal ordered ${daysSinceOrder} days ago — no completion yet` };
  }

  return null;
};

// ── Date group definitions ──
const buildGroups = (loan, keyDates, isPurchase) => {
  const groups = [];

  // Rate Lock
  groups.push({
    key: 'rateLock',
    title: 'Rate Lock',
    icon: 'ti-lock',
    dates: [
      { key: 'rateLockInitiated', label: 'Lock Initiated', value: keyDates.rateLockInitiatedDate || loan.lockRequestedDate },
      { key: 'rateLockExpires', label: 'Lock Expires', value: keyDates.rateLockExpiresDate || loan.lockExpirationDate },
    ],
  });

  // ECOA / Compliance
  groups.push({
    key: 'ecoa',
    title: 'ECOA / Compliance',
    icon: 'ti-shield-check',
    dates: [
      { key: 'ecoaTriggered', label: 'ECOA Triggered', value: keyDates.ecoaTriggeredDate },
      { key: 'ecoaSubmitted', label: 'ECOA Application Submitted', value: keyDates.ecoaApplicationSubmittedDate },
      { key: 'adverseAction', label: 'Adverse Action Sent', value: keyDates.adverseActionInitialSentDate },
      { key: 'counterofferSent', label: 'Counteroffer Sent', value: keyDates.counterofferSentDate },
      { key: 'counterofferExpires', label: 'Counteroffer Expires', value: keyDates.counterofferExpiresDate },
      { key: 'noticeOfIncompletenessInitialSentDate', label: 'NOI Sent', value: keyDates.noticeOfIncompletenessInitialSentDate },
      { key: 'noticeOfIncompletenessExpirationDate', label: 'NOI Expires', value: keyDates.noticeOfIncompletenessExpirationDate },
      { key: 'withdrawalNotice', label: 'Withdrawal Notice Sent', value: keyDates.withdrawalNoticeInitialSentDate },
    ],
  });

  // Loan Estimate
  groups.push({
    key: 'loanEstimate',
    title: 'Loan Estimate',
    icon: 'ti-file-text',
    dates: [
      { key: 'tridTriggered', label: 'TRID Triggered', value: keyDates.tridTriggeredDate },
      { key: 'loanEstimateNeedsToBeSentBy', label: 'LE Needs to Be Sent By', value: keyDates.loanEstimateNeedsToBeSentByDate },
      { key: 'initialLeSent', label: 'Initial LE Sent', value: keyDates.initialLoanEstimateSentDate },
      { key: 'leExpiration', label: 'LE Expiration', value: keyDates.loanEstimateExpirationDate },
      { key: 'intentToProceed', label: 'Intent to Proceed', value: keyDates.intentToProceedDate },
      { key: 'initialLeReceived', label: 'Initial LE Received', value: keyDates.initialLoanEstimateReceivedDate },
      { key: 'initialLeSigned', label: 'Initial LE Signed', value: keyDates.initialLoanEstimateSignedDate },
      { key: 'earliestAllowedClosing', label: 'Earliest Allowed Closing', value: keyDates.earliestAllowedClosingDate },
    ],
  });

  // Closing Disclosure
  groups.push({
    key: 'closingDisclosure',
    title: 'Closing Disclosure',
    icon: 'ti-file-certificate',
    dates: [
      { key: 'initialCdSent', label: 'Initial CD Sent', value: keyDates.initialClosingDisclosureSentDate },
      { key: 'cdElectronicToPaper', label: 'CD Electronic to Paper', value: keyDates.closingDisclosureElectronicToPaperDate },
      { key: 'cdReceived', label: 'CD Received', value: keyDates.closingDisclosureReceivedDate },
      { key: 'cdSigned', label: 'CD Signed', value: keyDates.closingDisclosureSignedDate },
    ],
  });

  // Credit
  groups.push({
    key: 'credit',
    title: 'Credit',
    icon: 'ti-report-analytics',
    dates: [
      { key: 'creditPulled', label: 'Credit Pulled', value: keyDates.creditPulledDate },
      { key: 'creditRefreshPulled', label: 'Credit Refresh Pulled', value: keyDates.creditRefreshPulledDate },
      { key: 'uimOrderedDate', label: 'UIM Ordered', value: keyDates.uimOrderedDate },
    ],
  });

  // Appraisals
  groups.push({
    key: 'appraisals',
    title: 'Appraisals',
    icon: 'ti-home-search',
    dates: [
      { key: 'appraisalOrdered', label: 'Appraisal Ordered', value: keyDates.appraisalOrderedDate },
      { key: 'inspectionScheduled', label: 'Inspection Scheduled', value: keyDates.inspectionScheduledDate },
      { key: 'inspectionCompleted', label: 'Inspection Completed', value: keyDates.inspectionCompletedDate },
      { key: 'appraisalCompleted', label: 'Appraisal Completed', value: keyDates.appraisalCompletedDate },
      { key: 'appraisalSentToLender', label: 'Sent to Lender', value: keyDates.appraisalSentToLenderDate },
      { key: 'deliveredToBorrower', label: 'Delivered to Borrower', value: keyDates.appraisalDeliveredToBorrowerDate },
      { key: 'appraisalPaid', label: 'Appraisal Paid', value: keyDates.appraisalPaidDate },
    ],
  });

  // Title & Insurance
  groups.push({
    key: 'titleInsurance',
    title: 'Title & Insurance',
    icon: 'ti-certificate',
    dates: [
      { key: 'titleOrdered', label: 'Title Ordered', value: keyDates.titleOrderedDate },
      { key: 'titleCommitmentReceived', label: 'Title Commitment Received', value: keyDates.titleCommitmentReceivedDate },
      { key: 'homeInsuranceOrdered', label: 'Home Insurance Ordered', value: keyDates.homeInsuranceOrderedDate },
      { key: 'homeInsuranceReceived', label: 'Home Insurance Received', value: keyDates.homeInsuranceReceivedDate },
    ],
  });

  // PMI
  groups.push({
    key: 'pmi',
    title: 'Private Mortgage Insurance',
    icon: 'ti-shield-dollar',
    dates: [
      { key: 'pmiQuoted', label: 'PMI Quoted', value: keyDates.pmiQuotedDate },
      { key: 'pmiOrdered', label: 'PMI Ordered', value: keyDates.pmiOrderedDate },
      { key: 'orderCompleted', label: 'Order Completed', value: keyDates.pmiOrderCompletedDate },
    ],
  });

  // Closing & Funding (Purchase shows contract date)
  const closingDates = [
    { key: 'clearedToClose', label: 'Cleared to Close', value: keyDates.clearedToCloseDate },
    { key: 'closingPackageSent', label: 'Closing Package Sent', value: keyDates.closingPackageSentDate },
    { key: 'closingDate', label: 'Closing Date', value: keyDates.closingDate || loan.estimatedCloseDate },
    { key: 'executedClosingPackageReceived', label: 'Executed Package Received', value: keyDates.executedClosingPackageReceivedDate },
    { key: 'fundsOrdered', label: 'Funds Ordered', value: keyDates.fundsOrderedDate },
    { key: 'funded', label: 'Funded', value: keyDates.fundedDate },
    { key: 'actualDisbursement', label: 'Actual Disbursement', value: keyDates.actualDisbursementDate },
    { key: 'firstPayment', label: 'First Payment Date', value: keyDates.firstPaymentDate },
  ];

  groups.push({
    key: 'closingFunding',
    title: 'Closing & Funding',
    icon: 'ti-cash',
    dates: closingDates,
  });

  return groups;
};

const KeyDatesCard = ({ loanDetail }) => {
  const loan = loanDetail?.loan || {};
  const keyDates = loanDetail?.keyDatesObj || {};
  const isPurchase = loan.loanPurpose === 'Purchase';
  const [expandedGroups, setExpandedGroups] = useState({});

  const groups = useMemo(() => buildGroups(loan, keyDates, isPurchase), [loan, keyDates, isPurchase]);

  // Compute urgencies across all dates
  const urgencies = useMemo(() => {
    const results = [];
    for (const group of groups) {
      for (const date of group.dates) {
        if (!date.value) continue;
        const u = getUrgency(date.key, date.value, loanDetail);
        if (u) results.push({ ...u, dateKey: date.key, dateLabel: date.label, dateValue: date.value, groupKey: group.key });
      }
    }
    return results.sort((a, b) => b.score - a.score);
  }, [groups, loanDetail]);

  const topUrgency = urgencies[0] || null;

  // Auto-expand groups that have urgent items
  const urgentGroupKeys = useMemo(() => new Set(urgencies.map(u => u.groupKey)), [urgencies]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isExpanded = (key) => {
    if (expandedGroups[key] !== undefined) return expandedGroups[key];
    return urgentGroupKeys.has(key);
  };

  // Filter groups to only show those with at least one populated date
  const visibleGroups = groups.filter(g => g.dates.some(d => d.value));

  return (
    <Box>
      {/* Next Action Callout */}
      {topUrgency && (
        <Alert severity={topUrgency.level === 'danger' ? 'error' : 'warning'} sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Next Action Needed</Typography>
          <Typography variant="body2">{topUrgency.message}</Typography>
          <Typography variant="caption" color="text.secondary">{topUrgency.dateLabel}: {formatDate(topUrgency.dateValue)}</Typography>
        </Alert>
      )}

      {visibleGroups.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No key dates available</Typography>
          <Typography variant="body2" color="text.secondary">Dates will appear here as the loan progresses through the pipeline.</Typography>
        </Box>
      )}

      {visibleGroups.map(group => {
        const populatedDates = group.dates.filter(d => d.value);
        const pendingDates = group.dates.filter(d => !d.value);
        const expanded = isExpanded(group.key);
        const hasUrgent = urgencies.some(u => u.groupKey === group.key);

        return (
          <Card variant="outlined" key={group.key} sx={{ mb: 1, ...(hasUrgent ? { borderColor: 'warning.main' } : {}) }}>
            <CardHeader
              onClick={() => toggleGroup(group.key)}
              sx={{ cursor: 'pointer', userSelect: 'none', py: 1.5 }}
              title={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">{group.title}</Typography>
                  {hasUrgent && <Chip label="Action Needed" size="small" color="warning" variant="outlined" />}
                  <Chip label={populatedDates.length} size="small" variant="outlined" />
                </Stack>
              }
              action={expanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
            />
            <Collapse in={expanded}>
              <Table size="small">
                <TableBody>
                  {populatedDates.map(date => {
                    const rel = relativeTime(date.value);
                    const u = urgencies.find(u => u.dateKey === date.key);
                    return (
                      <TableRow key={date.key}>
                        <TableCell sx={{ width: '35%' }}><Typography variant="body2" fontWeight={700}>{date.label}</Typography></TableCell>
                        <TableCell sx={{ width: '25%' }}><Typography variant="body2" color="text.secondary">{formatDate(date.value)}</Typography></TableCell>
                        <TableCell sx={{ width: '20%' }}>{rel && <Chip label={rel.text} size="small" color={rel.color} variant="outlined" />}</TableCell>
                        <TableCell sx={{ width: '20%' }}>
                          {u && <Chip icon={u.level === 'danger' ? <ErrorIcon /> : <WarningIcon />} label={u.level === 'danger' ? 'Urgent' : 'Attention'} size="small" color={u.level === 'danger' ? 'error' : 'warning'} variant="outlined" />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {pendingDates.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={4}><Typography variant="caption" color="text.secondary">{pendingDates.length} pending: {pendingDates.map(d => d.label).join(', ')}</Typography></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Collapse>
          </Card>
        );
      })}
    </Box>
  );
};

export default KeyDatesCard;
